/**
 * Service for editing PIM (Translate Around Me) tier capability overrides via the
 * platform admin API.
 *
 * Sprint P8c — three endpoints:
 *  - GET    /api/platform/pim-tiers                       — list all 3 tiers (default + override + effective)
 *  - PUT    /api/platform/pim-tiers/{tierName}            — write/replace override
 *  - DELETE /api/platform/pim-tiers/{tierName}/overrides  — clear override (revert to code default)
 *
 * Uses the global `fetch` API directly (not the `fetchWithAuth` wrapper) so that
 * tests can mock these endpoints independently of other admin requests.
 * The JWT bearer token is read from jwtService and attached manually.
 */

import { getAuthHeader } from '../utils/jwtService';

const getBaseUrl = () => {
    const fromEnv = process.env.REACT_APP_FUNCTIONS_URL;
    if (fromEnv) {
        return fromEnv.endsWith('/') ? fromEnv.slice(0, -1) : fromEnv;
    }
    return 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api';
};

const buildHeaders = () => ({
    'Content-Type': 'application/json',
    ...getAuthHeader(),
});

const pimTierApiService = {
    /**
     * Fetches all 3 PIM tiers with default + override + effective merged caps.
     * @returns {Promise<{ tiers: Array<object> }>}
     */
    listTiers: async () => {
        const url = `${getBaseUrl()}/platform/pim-tiers`;
        const response = await fetch(url, {
            method: 'GET',
            headers: buildHeaders(),
        });
        if (!response.ok) {
            const body = await response.text().catch(() => response.statusText);
            throw new Error(`Failed to load PIM tiers: ${response.status} ${body}`);
        }
        return response.json();
    },

    /**
     * Upserts the override for the specified tier.
     * @param {string} tierName Tier name (case-insensitive: "starter", "plus", "pro").
     * @param {object} overrideBody Partial override (any subset of MaxActiveRace, AllowedModels, DefaultModel, HedgeEnabled, RetailPricePerHourCents).
     * @returns {Promise<object>} The persisted override document.
     */
    saveOverride: async (tierName, overrideBody) => {
        const url = `${getBaseUrl()}/platform/pim-tiers/${encodeURIComponent(tierName.toLowerCase())}`;
        const response = await fetch(url, {
            method: 'PUT',
            headers: buildHeaders(),
            body: JSON.stringify(overrideBody),
        });
        if (!response.ok) {
            const body = await response.text().catch(() => response.statusText);
            throw new Error(`Failed to save PIM tier override: ${response.status} ${body}`);
        }
        return response.json();
    },

    /**
     * Deletes the override for the specified tier (revert to code default).
     * Idempotent — succeeds even if no override existed.
     * @param {string} tierName Tier name (case-insensitive).
     * @returns {Promise<object>}
     */
    clearOverride: async (tierName) => {
        const url = `${getBaseUrl()}/platform/pim-tiers/${encodeURIComponent(tierName.toLowerCase())}/overrides`;
        const response = await fetch(url, {
            method: 'DELETE',
            headers: buildHeaders(),
        });
        if (!response.ok) {
            const body = await response.text().catch(() => response.statusText);
            throw new Error(`Failed to clear PIM tier override: ${response.status} ${body}`);
        }
        return response.json();
    },
};

export default pimTierApiService;
